import React, { useState, useEffect } from 'react';
import {
  makeStyles,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Box,
  Typography,
  Divider,
  ListItemIcon,
  ListItemText,
  InputBase
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import NotificationsIcon from '@material-ui/icons/Notifications';
import PersonIcon from '@material-ui/icons/Person';
import SettingsIcon from '@material-ui/icons/Settings';
import MenuIcon from '@material-ui/icons/Menu';
import MenuOpenIcon from '@material-ui/icons/MenuOpen';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { microsoftAuthApiRef, identityApiRef, useApi } from '@backstage/core-plugin-api';
import LogoFull from '../Root/LogoFull';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    backgroundColor: '#ffffff',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  collapseButton: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    backgroundColor: '#f0f0f0',
    borderRadius: '8px',
    padding: '8px',
    width: '42px',
    height: '42px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease-in-out',
    zIndex: 1200,
    '&:hover': {
      backgroundColor: '#e0e0e0',
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  menuButton: {
    marginRight: theme.spacing(1),
    color: '#1c2c50',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  menuButtonDesktop: {
    marginRight: theme.spacing(1),
    color: '#1c2c50',
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
    },
  },
  hiddenOnMobile: {
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  toolbar: {
    minHeight: 64,
    padding: '0 16px',
    [theme.breakpoints.up('sm')]: {
      padding: '0 24px',
    },
  },
  logo: {
    marginRight: theme.spacing(4),
    height: 40,
  },
  title: {
    flexGrow: 1,
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'block',
    },
  },
  search: {
    position: 'relative',
    borderRadius: '20px',
    backgroundColor: '#f5f5f5',
    transition: 'width 0.3s ease-in-out',
    '&:hover': {
      backgroundColor: '#f0f0f0',
      cursor: 'pointer',
      width: '300px',
    },
    marginLeft: theme.spacing(1),
    width: '40px',
    alignSelf: 'center',
  },
  searchExpanded: {
    width: '300px',
  },
  searchCollapsed: {
    width: '40px',
    overflow: 'hidden',
  },
  searchIcon: {
    padding: theme.spacing(0, 1),
    height: '100%',
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#9E9E9E',
    zIndex: 1,
    cursor: 'pointer',
    width: '40px',
  },
  inputRoot: {
    width: '100%',
    color: '#9E9E9E',
    cursor: 'pointer',
  },
  inputInput: {
    padding: theme.spacing(1.5, 1, 1.5, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)}px)`,
    transition: theme.transitions.create('width'),
    width: '100%',
    opacity: 0,
    '&:focus': {
      opacity: 1,
    },
  },
  inputVisible: {
    opacity: 1,
  },
  searchModal: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: theme.spacing(1),
    zIndex: 1300,
  },
  searchModalContent: {
    borderRadius: 8,
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    width: '100%',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: '#ffffff',
    border: '1px solid #eaeaea',
  },
  searchBar: {
    borderBottom: '1px solid #eaeaea',
    '& > div': {
      maxWidth: '100%',
    },
  },
  searchBarInput: {
    fontSize: '1rem',
  },
  searchResultItem: {
    padding: theme.spacing(1),
    borderRadius: 4,
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  errorBox: {
    backgroundColor: '#fdecea',
    padding: theme.spacing(2),
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
  },
  filterBox: {
    padding: theme.spacing(2),
    borderTop: '1px solid #eaeaea',
  },
  resultsTitle: {
    color: '#666',
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
  viewAllButton: {
    textTransform: 'none',
    fontWeight: 500,
  },
  searchButton: {
    padding: 4,
    color: 'white',
    backgroundColor: '#1c2c50',
    borderRadius: '50%',
    marginRight: '4px',
    '&:hover': {
      backgroundColor: '#2c3c60',
    },
  },
  profileButton: {
    padding: 0,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  avatar: {
    backgroundColor: '#1c2c50',
    color: 'white',
    width: 42,
    height: 42,
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    border: '2px solid #FFDD00',
  },
  avatarIcon: {
    fontSize: '1.5rem',
    color: 'white',
  },
  menu: {
    marginTop: theme.spacing(5),
  },
  menuItem: {
    padding: theme.spacing(1),
  },
  listItemIcon: {
    minWidth: 36,
  },
  dialogTitle: {
    backgroundColor: '#1c2c50',
    color: 'white',
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  profileSection: {
    marginBottom: theme.spacing(3),
  },
  profileName: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  profileEmail: {
    color: '#666',
    marginBottom: theme.spacing(2),
  },
  settingsLink: {
    color: '#1c2c50',
    textDecoration: 'none',
    display: 'block',
    marginBottom: theme.spacing(1),
    '&:hover': {
      textDecoration: 'underline',
    },
  }
}));

interface CustomHeaderProps {
  isMenuCollapsed?: boolean;
  toggleMenuCollapse?: () => void;
}

export const CustomHeader: React.FC<CustomHeaderProps> = ({
  isMenuCollapsed,
  toggleMenuCollapse
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const microsoftAuthApi = useApi(microsoftAuthApiRef);
  const identityApi = useApi(identityApiRef);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [isMouseOverSearch, setIsMouseOverSearch] = useState(false);
  const searchRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  
  // Efecto para manejar clics fuera del buscador
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        // Si el clic es fuera del buscador, cerrar el buscador
        setIsSearchExpanded(false);
      }
    };
    
    // Agregar listener para detectar clics fuera del buscador
    document.addEventListener('mousedown', handleClickOutside);
    
    // Limpiar listener al desmontar
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Efecto para cerrar el buscador al cambiar de ruta
  useEffect(() => {
    // Función para limpiar el buscador
    const cleanupSearch = () => {
      // Cerrar el buscador
      setIsSearchExpanded(false);
      setIsMouseOverSearch(false);
      setSearchTerm('');
      
      // Remover el foco del input
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      
      // Asegurarnos de que el input específico pierda el foco
      if (inputRef.current) {
        inputRef.current.blur();
      }
    };
    
    // Limpiar al montar y cada vez que se desmonte
    cleanupSearch();
    return cleanupSearch;
  }, [navigate]);
  
  // Estado para la información del usuario
  const [userInfo, setUserInfo] = useState<{
    name: string;
    email: string;
    photoUrl: string;
  }>({ 
    name: '',
    email: '',
    photoUrl: '',
  });
  
  // Función para manejar la búsqueda y navegación a la página de resultados
  const handleSearch = () => {
    if (searchTerm.trim()) {
      navigate(`/search?query=${encodeURIComponent(searchTerm)}&types%5B%5D=software-catalog`);
      setSearchTerm('');
    }
  };
  
  // Manejar la tecla Enter en el campo de búsqueda
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };
  
  // Cargar la información del usuario desde la sesión
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        // Obtener identidad del usuario desde la API de identidad de Backstage
        const identity = await identityApi.getBackstageIdentity();
        const profile = await identityApi.getProfileInfo();
        
        // Obtener nombre y email del usuario
        const userName = profile.displayName || 'Usuario';
        // Corregir la obtención del correo para asegurar que se recupere correctamente
        let userEmail = '';
        
        // Intentar obtener el correo desde el profile primero ya que es más confiable
        if (profile.email) {
          userEmail = profile.email;
        } 
        // Si no está en profile, intentar extraerlo del userEntityRef
        else if (identity.userEntityRef && identity.userEntityRef.includes(':')) {
          // Convertir 'user:default/fvochoa_pichincha.com' a 'fvochoa@pichincha.com'
          const emailPart = identity.userEntityRef.split(':')[1];
          if (emailPart && emailPart.includes('/')) {
            const username = emailPart.split('/')[1];
            if (username && username.includes('_')) {
              // Reemplazar underscore por @ para formar el correo
              userEmail = username.replace('_', '@');
            } else {
              userEmail = username || '';
            }
          }
        }
        
        // Verificar explícitamente si hay una URL de imagen válida
        // Un string vacío se considerará como que no hay imagen
        const photoUrl = profile.picture && profile.picture.trim() !== '' ? profile.picture : '';
        
        setUserInfo({
          name: userName.toUpperCase(),
          email: userEmail,
          photoUrl,
        });
        
      } catch (error) {
        console.error('Error al obtener información del usuario:', error);
        // Si hay un error, establecer un estado para mostrar el avatar genérico
        setUserInfo({
          name: 'Usuario',
          email: '',
          photoUrl: '',
        });
      }
    };
    
    fetchUserInfo();
  }, [identityApi]);

  

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  // Funciones de manejo de perfil simplificadas

  const handleSignOut = async () => {
    handleProfileMenuClose();
    try {
      await microsoftAuthApi.signOut();
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <AppBar position="fixed" className={classes.root}>
      <Toolbar className={classes.toolbar}>
        {/* Eliminado icono de hamburguesa */}
        
        <RouterLink to="/">
          <LogoFull />
        </RouterLink>
        
        {toggleMenuCollapse && (
          <IconButton
            color="inherit"
            onClick={toggleMenuCollapse}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleMenuCollapse();
              }
            }}
            className={classes.collapseButton}
            style={{
              position: 'absolute',
              left: '240px', // Fijo en el ancho del menú expandido
              transform: 'translateX(-52px)', // Centrar el botón teniendo en cuenta su tamaño
              marginLeft: 0
            }}
            size="medium"
            aria-label={isMenuCollapsed ? 'Expandir menú lateral' : 'Colapsar menú lateral'}
            aria-expanded={!isMenuCollapsed}
            aria-controls="sidebar-menu"
            title={isMenuCollapsed ? 'Expandir menú' : 'Colapsar menú'}
          >
            {isMenuCollapsed ? <MenuOpenIcon style={{ color: '#1c2c50', fontSize: '24px' }} /> : <MenuIcon style={{ color: '#1c2c50', fontSize: '24px' }} />}
          </IconButton>
        )}
        
        <div style={{ flexGrow: 1 }} />
        
        <div 
          className={`${classes.search} ${isSearchExpanded ? classes.searchExpanded : classes.searchCollapsed}`}
          ref={searchRef}
          onMouseEnter={() => {
            setIsMouseOverSearch(true);
            setIsSearchExpanded(true);
            // Poner el foco automáticamente en el campo de búsqueda al pasar el ratón
            setTimeout(() => {
              if (inputRef.current) {
                inputRef.current.focus();
              }
            }, 100); // Pequeño retraso para que el campo se expanda primero
          }}
          onMouseLeave={() => {
            setIsMouseOverSearch(false);
            // Pequeño retraso para evitar problemas con eventos de clic
            setTimeout(() => {
              if (!isMouseOverSearch) {
                setIsSearchExpanded(false);
                setSearchTerm('');
              }
            }, 200);
          }}
          style={{ marginRight: 12 }}
        >
          <div className={classes.searchIcon} onClick={() => {
            if (!isSearchExpanded) {
              setIsSearchExpanded(true);
            } else {
              handleSearch();
            }
          }}>
            <SearchIcon style={{ color: '#1c2c50' }} />
          </div>
          {/* <div className={classes.inputRoot}>
            <InputBase
              placeholder="Buscar..."
              classes={{
                root: classes.inputRoot,
                input: `${classes.inputInput} ${isSearchExpanded ? classes.inputVisible : ''}`
              }}
              value={searchTerm}
              inputRef={inputRef}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSearchTerm(e.target.value);
                if (e.target.value) setIsSearchExpanded(true);
              }}
              onKeyPress={handleKeyPress}
              onFocus={() => setIsSearchExpanded(true)}
              onBlur={() => {
                // Añadir un pequeño retraso para evitar conflictos con otros eventos
                setTimeout(() => {
                  // Cerrar solo si el ratón no está sobre el componente
                  if (!isMouseOverSearch) {
                    setIsSearchExpanded(false);
                    setSearchTerm('');
                  }
                }, 200);
              }}
              endAdornment={
                isSearchExpanded ? (
                  <IconButton size="small" onClick={handleSearch} className={classes.searchButton}>
                    <ArrowForwardIcon fontSize="small" />
                  </IconButton>
                ) : null
              }
              inputProps={{ 'aria-label': 'buscar' }}
            />
          </div> */}
        </div>
        
        {/* <IconButton
          aria-label="notificaciones"
          onClick={() => navigate('/notifications')}
          style={{ marginRight: 12 }}
        >
          <NotificationsIcon style={{ color: '#1c2c50', fontSize: '24px' }} />
        </IconButton> */}
        
        <IconButton 
          aria-label="profile" 
          aria-controls="profile-menu" 
          aria-haspopup="true"
          onClick={handleProfileMenuOpen}
          className={classes.profileButton}
        >
          {userInfo.photoUrl ? (
            <Avatar 
              className={classes.avatar}
              src={userInfo.photoUrl}
              alt={userInfo.name || 'Usuario'}
            />
          ) : (
            <Avatar className={classes.avatar} style={{ backgroundColor: '#1c2c50', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PersonIcon style={{ color: 'white', fontSize: '1.5rem' }} />
            </Avatar>
          )}
        </IconButton>
        
        <Menu
          id="profile-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
          className={classes.menu}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box p={1}>
            <Typography variant="body2" style={{ fontWeight: 'bold' }}>{userInfo.name}</Typography>
            <Typography variant="caption" style={{ color: '#666' }}>{userInfo.email}</Typography>
          </Box>
          <MenuItem 
            component={RouterLink} 
            to="/settings" 
            onClick={handleProfileMenuClose}
            className={classes.menuItem}
          >
            <ListItemIcon className={classes.listItemIcon}>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Profile" />
          </MenuItem>
          <Divider style={{ margin: '4px 0' }} />
          <MenuItem 
            onClick={handleSignOut}
            className={classes.menuItem}
          >
            <ListItemIcon className={classes.listItemIcon}>
              <ExitToAppIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Sign Out" />
          </MenuItem>
        </Menu>
      </Toolbar>

      {/* Eliminado el diálogo de perfil ya que no se utiliza */}
    </AppBar>
  );
};
